import { Button, makeStyles, Typography } from '@material-ui/core';
import PlayArrowRounded from '@material-ui/icons/PlayArrowRounded';
import { blockedStatus, enStatus, ILesson } from 'interface/course';
import { dateFormater } from 'utils';
import { useHistory, useLocation } from 'react-router';
import defaultImage from 'assets/img/default-lesson.png';
import { getCdnUrl } from 'utils';
import { SvgIcon } from 'components/Shared/SvgIcon';

interface IProps {
  lesson: ILesson;
  blockedModule: boolean;
  disableProgress?: boolean;
}

const useStyles = makeStyles((theme) => ({
  lessonContainer: {
    paddingBottom: theme.spacing(10),
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    '& *[data-blocked=true]': {
      opacity: 0.5,
      pointerEvents: 'none',
    },
    '& img': {
      '@media(max-width: 600px)': {
        display: 'none',
      },
      minWidth: 160,
      maxWidth: 160,
      height: 120,
      borderRadius: 8,
      objectFit: 'cover',
    },
    '&:hover': {
      cursor: 'pointer',
    },
  },
  lessonTextContainer: {
    '& > h4': {
      color: theme.palette.common.white,
      whiteSpace: 'normal',
      overflow: 'hidden',
      display: '-webkit-box',
      wordBreak: 'break-word',
      '-webkit-line-clamp': 4,
      '-webkit-box-orient': 'vertical',
    },
    '& > h4:first-child': {
      marginBottom: theme.spacing(4),
      fontWeight: 600,
    },
    padding: theme.spacing(4),
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    '@media(max-width: 425px)': {
      paddingLeft: 0,
      '& h4': {
        '&:first-child': {
          fontSize: 16,
          marginBottom: theme.spacing(2),
        },
        fontSize: 12,
      },
    },
  },
  button: {
    minWidth: 80,
    textTransform: 'capitalize',
    border: `1px solid ${theme.palette.common.white}`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.common.white,
    padding: theme.spacing(2, 4),
    paddingLeft: theme.spacing(5),
    '& svg': {
      color: theme.palette.primary.main,
    },
  },
  watchedIcon: {
    '@media(max-width: 425px)': {
      display: 'none',
    },
    display: 'flex',
    background: theme.palette.safevideo.main,
    width: 20,
    height: 20,
    marginLeft: 3,
    marginRight: theme.spacing(6),
    zIndex: 10,
    '& svg': {
      fontSize: 26,
      color: theme.palette.common.white,
    },
  },
}));

const Lesson = ({ lesson, blockedModule, disableProgress }: IProps) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const getMessage = (lesson: ILesson) => {
    const messageMapper = {
      [enStatus.BLOCKED]: 'Você ainda não possui acesso a essa aula',
      [enStatus.EXPIRED]: 'Essa aula está expirada',
      [enStatus.NOT_RELEASED]: lesson.release
        ? `Essa aula será liberada à partir do dia ${dateFormater(lesson.release)}`
        : 'Essa aula está bloqueada, aguarde a liberação',
    };

    return messageMapper[lesson.status] ?? lesson.short_description;
  };

  const goToLesson = (lesson: ILesson) => () => {
    const courseUrl = location.pathname.replace(/\/$/, '');
    history.push(`${courseUrl}/aula/${lesson.id}`);
  };

  return (
    <div
      className={classes.lessonContainer}
      key={lesson.id}
      onClick={goToLesson(lesson)}
      id='course-lesson-lessonContainer'
    >
      {!disableProgress && (
        <div aria-label='indicador de aula assistida' className={classes.watchedIcon}>
          {lesson.watched && <SvgIcon aria-label='aula assistida' name='watched-lesson' />}
          {!lesson.watched && <SvgIcon aria-label='aula não assistida' name='watch-lesson' />}
        </div>
      )}
      <img src={getCdnUrl(lesson.image) || defaultImage} alt='capa da aula' />
      <div
        data-blocked={blockedStatus.includes(lesson.status) || blockedModule}
        className={classes.lessonTextContainer}
      >
        <Typography aria-label='título da aula' variant='h4' id='course-lesson-lessonTitle'>
          {lesson.title}
        </Typography>
        <Typography aria-label='descrição curta da aula' variant='h4' id='course-lesson-lessonDescription'>
          {getMessage(lesson)}
        </Typography>
      </div>
      <Button
        aria-label='Botão acessar a aula'
        data-blocked={blockedStatus.includes(lesson.status) || blockedModule}
        className={classes.button}
        onClick={goToLesson(lesson)}
        id='course-lesson-btn-goToLesson'
      >
        Acessar <PlayArrowRounded />
      </Button>
    </div>
  );
};

export default Lesson;
